import { Ref, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { Review, ReviewCategory } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";

import ReviewCategoryItem from "./ReviewCategoryItem";

export type ReviewCategoriesListRef = {
  highlight: (name: string | undefined) => void;
};

type ReviewCategoriesListProps = {
  review: Review;
  onHover?: (key: string | undefined) => void;
  ref?: Ref<ReviewCategoriesListRef>;
};

function ReviewCategoriesList({
  review,
  onHover,
  ref,
}: ReviewCategoriesListProps) {
  const { t } = useTranslation(["formatter"]);
  const { reviewId, admin } = useParams();
  const { review_categories: reviewCategoriesData } = review;

  const [highlightedItem, setHighlightedItem] = useState<string | undefined>(
    undefined
  );

  const reviewCategoriesUnit = getCarbonDisplayUnit(
    reviewCategoriesData.map((reviewCategory) => reviewCategory.carbon)
  );

  useImperativeHandle(ref, () => {
    return {
      highlight(key: string | undefined) {
        setHighlightedItem(key);
      },
    };
  });

  return (
    <ul>
      {reviewCategoriesData
        .filter((item: ReviewCategory) => !!item.carbon)
        .map((item: ReviewCategory) => {
          const { formattedCarbon } = formatCarbon(
            t,
            item.carbon,
            reviewCategoriesUnit
          );

          return (
            <ReviewCategoryItem
              key={item.key}
              url={`/reviews/${reviewId}/review_categories/${item.id}${
                admin ? "/admin" : ""
              }`}
              categoryKey={item.key}
              carbonToDisplay={formattedCarbon}
              carbonUnit={reviewCategoriesUnit}
              variant={highlightedItem === item.key ? "highlighted" : "default"}
              onHover={onHover}
            />
          );
        })}
    </ul>
  );
}
ReviewCategoriesList.displayName = "ReviewCategoriesList";

export default ReviewCategoriesList;
